export type LicenseType = 'start' | 'premium' | 'free_trial';

/**
 * The license summary embedded in every `GET /schools` row. Present only for a license that is
 * valid right now — `null` means the school is in read-only mode. It deliberately omits
 * `revokedAt` / `active` / `schoolId` (meaningless for a license that is valid by definition);
 * the full history comes from `GET /schools/{schoolId}/licenses`.
 */
export interface ActiveLicense {
  id: string;
  type: LicenseType;
  validFrom: string;
  validTo: string;
  note: string | null;
  grantedCredits: number;
  usedCredits: number;
  remainingCredits: number;
}

export interface SchoolLicense extends ActiveLicense {
  schoolId: string;
  revokedAt: string | null;
  active: boolean;
  createdAt: string;
}

export interface SchoolLicenseData {
  type: LicenseType;
  validFrom: string;
  validTo: string;
  /** `null` = server default for the tier. Ignored on `PUT` — a changed pool must go through a grant. */
  aiCredits: number | null;
  note: string | null;
}

/** `licenseId === null` means the school has no valid license, and therefore no credit pool. */
export interface AiQuota {
  licenseId: string | null;
  grantedCredits: number;
  usedCredits: number;
  remainingCredits: number;
}

/**
 * What one AI call took out of the school's pool, carried by every AI response and shown next to
 * the result it paid for. Always present, always in credits — the same unit as {@link AiQuota},
 * whose own numbers are refetched from the server rather than kept up to date from here.
 *
 * The amount is known only after the model has answered, so it can never be quoted beforehand.
 * `0` means no pool absorbed the call (the license expired between the check and the charge, or
 * the accounting write rolled back); a call that reached the model costs at least 0.1, so a zero
 * is never a very cheap call.
 */
export interface AiCharged {
  credits: number;
}

export interface AiQuotaGrantData {
  credits: number;
  note: string | null;
}

/** `initial` is the pool the license started with, recorded by the server as a grant of its own. */
export type AiGrantSource = 'initial' | 'manual';

/**
 * One change of a license's credit pool. `GET …/ai-quota/grants` answers with the whole history as
 * a plain array, newest first — a pool grows by the license default plus the odd top-up, so unlike
 * the usage readings it is neither paged nor sortable.
 */
export interface AiQuotaGrant {
  id: string;
  licenseId: string;
  /** Signed and never zero — a negative row corrects an earlier grant. */
  credits: number;
  source: AiGrantSource;
  note: string | null;
  createdByName: string | null;
  createdAt: string;
}

export type AiOperation =
  | 'thematic_blocks'
  | 'block_from_curriculum'
  | 'block_chat'
  | 'sovu'
  | 'profile_text'
  | 'ovu_recommendation'
  | 'ovu_placement'
  | 'ovu_placement_reasons';

/**
 * One AI call. `credits` is what the school was charged and `model` which model answered — the
 * school picks that one itself in its AI settings, so it reads back what its choice actually ran
 * on. The token counts and the supplier cost behind the charge stay ours: they are sent to system
 * admins only, and are *absent* rather than null for anyone else.
 */
export interface AiUsageEntry {
  id: string;
  licenseId: string;
  operation: AiOperation;
  /** `0` is a real value: the license expired between the pre-flight check and the charge. */
  credits: number;
  model: string;
  inputTokens?: number;
  outputTokens?: number;
  thinkingTokens?: number;
  estimatedCost?: number;
  userName: string | null;
  createdAt: string;
}

/**
 * A value handed back by `mat-datepicker`, kept opaque: the app runs the Luxon adapter, and `luxon`
 * ships no type declarations. Its `toString()` is ISO, which is all the date filters need.
 */
export type PickerDate = { toString(): string };

/**
 * A dimension a statistics row may be summed by — one element of the `groupBy` set, in its wire
 * spelling. The label order is the column order.
 */
export type AiStatisticsDimension = 'day' | 'user' | 'operation';

export const AI_STATISTICS_DIMENSION_LABELS: Record<AiStatisticsDimension, string> = {
  day: 'Dne',
  user: 'Uživatele',
  operation: 'Akce',
};

/**
 * The `groupBy` value for no dimension at all — the whole window in one row. A word rather than an
 * empty set because the server reads an empty `groupBy=` as its default, not as "nothing".
 */
export const AI_STATISTICS_NO_GROUPING = 'none';

/**
 * The calls summed by the server over the same filters as the log, per user, per operation, or
 * both — within one Europe/Prague calendar day, or over the whole window when not grouped by day. A
 * dimension not grouped by is absent from the row. The admin-only group is absent, not null, for
 * everyone else — same rule as {@link AiUsageEntry}.
 */
export interface AiUsageStatisticsRow {
  userId?: string;
  userName?: string | null;
  operation?: AiOperation;
  /** `YYYY-MM-DD`, already in Czech time — rendered as is, never re-zoned. */
  date?: string;
  calls: number;
  credits: number;
  inputTokens?: number;
  outputTokens?: number;
  thinkingTokens?: number;
  estimatedCost?: number;
}

/**
 * A property of {@link AiUsageEntry} the log may be ordered by, in its wire spelling — the value
 * of the `sort` param. The last one is served to system admins only, and the server refuses to
 * sort by it for anyone else (`USAGE_SORT_UNKNOWN`), so it is offered only where the column is.
 */
export type AiUsageSortProperty =
  | 'createdAt'
  | 'operation'
  | 'userName'
  | 'credits'
  | 'estimatedCost';

export interface AiUsageSort {
  property: AiUsageSortProperty;
  direction: 'asc' | 'desc';
}

export const AI_OPERATION_LABELS: Record<AiOperation, string> = {
  thematic_blocks: 'Generování tematických bloků',
  block_from_curriculum: 'Generování bloku z učiva',
  block_chat: 'Asistent u tematického bloku',
  sovu: 'Generování ŠOVU',
  profile_text: 'Generování profilu předmětu',
  // Flat 1 credit from the RVP similarity service, so its rows carry no tokens and no supplier cost.
  ovu_recommendation: 'Doporučení OVU',
  ovu_placement: 'Hledání bloků pro OVU',
  ovu_placement_reasons: 'Zdůvodnění návrhu bloků',
};

export const LICENSE_TYPE_LABELS: Record<LicenseType, string> = {
  start: 'START',
  premium: 'PREMIUM',
  free_trial: 'ZKUŠEBNÍ',
};

/**
 * Default credit pools, shown as a hint in the create dialog. Mirrors the server configuration,
 * which a system admin may override per license — never the value a screen renders.
 */
export const DEFAULT_CREDITS: Record<LicenseType, number> = {
  start: 5000,
  premium: 10000,
  free_trial: 100,
};

/**
 * Credits carry exactly one decimal place; the server refuses anything finer rather than truncating
 * it. Guards the two request bodies that take a credit amount.
 */
export function hasAtMostOneDecimal(value: number): boolean {
  const tenths = value * 10;
  // A tolerance rather than `Number.isInteger`: `x * 10` is not exact for every one-decimal value.
  return Math.abs(tenths - Math.round(tenths)) < 1e-9;
}

/** A license valid for fewer days than this is rendered as "expiring soon". */
export const EXPIRING_SOON_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * How many days the license still holds. `validTo` is the last day it is valid, parsed as UTC
 * midnight — i.e. that day's *start*. Validity runs to its end, so the whole day is added before
 * the remainder is counted; without it the count reads "0" throughout the final day.
 */
export function licenseDaysLeft(validTo: string, now = Date.now()): number {
  return Math.max(0, Math.ceil((new Date(validTo).getTime() + MS_PER_DAY - now) / MS_PER_DAY));
}

export type LicenseState = 'revoked' | 'active' | 'expired' | 'future';

/**
 * The row state shown in the history table. Order matters: a revoked license keeps its original
 * period, so `revokedAt` has to win over the dates.
 */
export function licenseState(license: SchoolLicense, today = new Date()): LicenseState {
  if (license.revokedAt !== null) return 'revoked';
  if (license.active) return 'active';
  // Both ends are checked: a period that has already started is over, never "future", whatever
  // the reason the server has for not marking it active.
  return new Date(license.validFrom) > today ? 'future' : 'expired';
}
